import {inject, Injectable} from "@angular/core"
import {DataObjectWithRelated} from "@cm/utils"
import {CM_COLORMASS_CLIENT} from "@common/config/client-injector"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {jobIsActive} from "@common/helpers/jobs/states"
import {GenerateThumbnailService} from "@common/services/generate-thumbnail/generate-thumbnail.service"
import {DataObjectThumbnailJobStatusGQL} from "@common/services/generate-thumbnail/image-data-object-thumbnail-trigger.service.generated"
import {CM_LOGGER_TEXTURES} from "@common/services/logging"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {JobState} from "@generated"

export type ImageDataObjectInfo = {
    dataObjectWithRelated: DataObjectWithRelated
    associatedTextureSetRevisionIds: string[]
}

export const isLegacyImageDataObject = (info: ImageDataObjectInfo) =>
    !info.dataObjectWithRelated.imageTilesDescriptor || info.dataObjectWithRelated.imageTilesDescriptor.tileChannels === undefined

@Injectable({
    providedIn: "root",
})
export class ImageDataObjectThumbnailTriggerService {
    private readonly generateThumbnails = inject(GenerateThumbnailService)
    private readonly dataObjectThumbnailJobStatus = inject(DataObjectThumbnailJobStatusGQL)
    private readonly notifications = inject(NotificationsService)
    private readonly logger = inject(CM_LOGGER_TEXTURES)
    private readonly isConfigurator = inject(CM_COLORMASS_CLIENT) === "configurator"

    private readonly alreadyTriggeredIds = new Set<string>()

    async triggerUpdateJobs(imageDataObjects: ImageDataObjectInfo[], args: {legacyOnly: boolean; skipFailed: boolean}): Promise<number> {
        if (this.isConfigurator) {
            return 0
        }

        // gather data objects to process, skipping IDs already triggered this session
        const dataObjectIdsToCheck = new Set<string>()
        for (const imageDataObjectInfo of imageDataObjects) {
            if (args.legacyOnly && !isLegacyImageDataObject(imageDataObjectInfo)) {
                continue
            }
            const id = imageDataObjectInfo.dataObjectWithRelated.id
            if (!this.alreadyTriggeredIds.has(id)) {
                dataObjectIdsToCheck.add(id)
            }
        }

        // for data objects, only trigger those without an actively running thumbnail job
        const dataObjectIdsToTrigger = new Set<string>()
        await Promise.all(
            Array.from(dataObjectIdsToCheck).map(async (id) => {
                try {
                    const result = await fetchThrowingErrors(this.dataObjectThumbnailJobStatus)({id})
                    if (!jobIsActive(result.dataObject.thumbnailProcessingJob ?? undefined)) {
                        if (!args.skipFailed || result.dataObject.thumbnailProcessingJob?.state !== JobState.Failed) {
                            dataObjectIdsToTrigger.add(id)
                        }
                    }
                } catch (error) {
                    this.logger.error(`Failed to check thumbnail job status for data object ${id}`, error)
                }
            }),
        )

        const dataObjectIds = Array.from(dataObjectIdsToTrigger)
        const triggered = dataObjectIds.length

        if (triggered === 0) {
            return 0
        }

        dataObjectIds.forEach((id) => this.alreadyTriggeredIds.add(id))

        try {
            await Promise.all(
                dataObjectIds.map((id) => {
                    this.logger.debug(`Creating thumbnails for data object ${id}`)
                    return this.generateThumbnails.createThumbnailForDataObject(id)
                }),
            )
        } catch (error) {
            this.logger.error("Failed to run data object update job mutation", error)
        }

        this.notifications.showInfo(`Triggered thumbnail generation for ${triggered} legacy material(s).`)
        return triggered
    }
}
